import React from 'react';
import {
    Card,
    CardActionArea,
    CardHeader,
    List,
    ListItemButton,
    ListItemText,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IMenuResponse } from '../../../model/base/response/menu-response';
import { useMenuLinkNavigation } from '../../../hooks/useMenuLinkNavigation';

interface MegaMenuCardProps {
    menu: IMenuResponse;
    onNavigate: () => void;
}

export const MegaMenuCard: React.FC<MegaMenuCardProps> = ({ menu, onNavigate }) => {
    const openMenuLink = useMenuLinkNavigation();
    const allItems = menu.items || [];

    const handleItemClick = (url: string | undefined, isExternal: boolean | undefined) => {
        if (!url) return;

        openMenuLink(url, isExternal);
        onNavigate();
    };

    const header = (
        <CardHeader
            title={
                <Typography variant="subtitle1" component="h3" sx={{ color: 'brand.text', lineHeight: 1.3 }}>
                    {menu.name}
                </Typography>
            }
            action={menu.url && menu.isExternal
                ? <OpenInNewIcon fontSize="small" sx={{ mt: 1, mr: 1, fontSize: '0.9rem' }} />
                : undefined}
            sx={{
                backgroundColor: 'brand.surface',
                borderBottom: 1,
                borderColor: 'divider',
                py: 1.5,
                px: 2,
            }}
        />
    );

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Menu de link direto (URL ou página customizada): o próprio card leva ao destino */}
            {menu.url ? (
                <CardActionArea onClick={() => handleItemClick(menu.url, menu.isExternal)}>
                    {header}
                    {menu.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                            {menu.description}
                        </Typography>
                    )}
                </CardActionArea>
            ) : header}
            <List dense disablePadding sx={{ flexGrow: 1 }}>
                {allItems.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => handleItemClick(item.url, item.isExternal)}
                        sx={{
                            py: 1,
                            px: 2,
                            minHeight: 44,
                            '&:hover': {
                                backgroundColor: 'brand.surface',
                                '& .MuiListItemText-primary': {
                                    color: 'brand.text',
                                },
                            },
                        }}
                    >
                        <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                                fontSize: '0.9375rem',
                            }}
                        />
                        {item.isExternal && (
                            <OpenInNewIcon
                                fontSize="small"
                                sx={{ ml: 1, fontSize: '0.9rem', color: 'text.secondary' }}
                            />
                        )}
                    </ListItemButton>
                ))}
            </List>
        </Card>
    );
};
